import { get, patch, post } from './http';
import { ApplicationStatus, TeamType } from './user-affiliations';
import type { Location, MediaFile } from './user-affiliations';

// Types sourced from https://github.com/ev-proddev/sign-on-web
// Paths below are relative to that repo root (src/main/java/com/experticity/signon/web/).
// UserDto and TeamOnboardingAffiliation originate from other repos — see their @source comments.

// ── Types ─────────────────────────────────────────────────────────────────────

/** @source ev-proddev/user — UserDto.java */
export type UserDto = {
  uuid: string;
  userId: number;
  primaryEmailId: string;
  firstName: string;
  lastName: string;
  username: string;
  status: string;
  passwordDisabled: boolean;
  passwordValidOnce: boolean;
  expertProfileVisibility: string;
  localeInfoId: number;
  addressId: number;
  contact: boolean;
  createdTime: number;
  createdBy: number;
  modifiedTime: number;
  modifiedBy: number;
  avatar: string;
};

/** @source ev-proddev/user-affiliations — JobFunctionDTO.java */
export type JobFunction = {
  jobFunctionId: number;
  description: string;
  jobKey: string;
};

/** @source ev-proddev/user-affiliations — TeamOnboardingAffiliationDTO.java */
export type TeamOnboardingAffiliation = {
  teamId: number;
  teamKey: string;
  name: string;
  shortName: string;
  teamType: TeamType;
  brandCount: number;
  memberCount: number;
  teamImage: MediaFile | null;
  teamImageOverride: MediaFile | null;
  publicApplicationFormId: number | null;
  renewalApplicationFormId: number | null;
  teamApplicationFormId: number | null;
  applicationsEnabled: boolean;
  applicationStatus: ApplicationStatus;
  status: string;
  expired: boolean;
  expiringSoon: boolean;
  employmentStatus: string;
  jobFunction: JobFunction | null;
  location: Location | null;
  memberNumber: string;
};

/** @sync-ignore — request body shape; backend class path unknown, find via /sync-api-types once auth is set up */
export type SignInRequest = {
  identifier: string;
  password: string;
};

/** @source model/SignInRestResponse.java */
export type SignInResponse = {
  error?: string;
  passwordResetToken?: string;
  user: UserDto;
};

/** @sync-ignore — request body shape; backend class path unknown, find via /sync-api-types once auth is set up */
export type SignUpRequest = {
  ageConfirmation?: boolean;
  contact?: boolean;
  countryCode?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  memberId?: string;
  password: string;
  state?: string;
  teamTargetId?: number;
  terms?: boolean;
};

/** @source model/SignUpRestResponse.java */
export type SignUpResponse = {
  error?: string;
  user: UserDto;
  existingActiveAffiliations?: TeamOnboardingAffiliation[];
  createdAffiliations?: TeamOnboardingAffiliation[];
  potentialAffiliations?: TeamOnboardingAffiliation[];
};

/** @source model/PersonalInfoBean.java */
export type PersonalInfoRequest = {
  addressId?: number;
  birthDate?: string;
  birthYear?: number;
  city?: string;
  countryCode?: string;
  countryCodeId?: number;
  email?: string;
  firstName?: string;
  gender?: string;
  /** @sync-ignore — not found in PersonalInfoBean; may be a legacy or frontend-only field */
  interest?: string;
  lastName?: string;
  state?: string;
  subdivisionId?: number;
};

/** @source model/OnboardingConfig.java */
export type OnboardingConfig = {
  analytics?: {
    appName?: string;
    gaId: string;
    collectorUrl: string;
  };
  messages?: Record<string, string>;
  toggles?: Record<string, boolean>;
  [key: string]: unknown;
};

// ── API ───────────────────────────────────────────────────────────────────────

export function postSignIn(request: SignInRequest): Promise<SignInResponse> {
  return post<SignInResponse>('/sign-on/service/sign-in', request);
}

export function postSignUp(request: SignUpRequest): Promise<SignUpResponse> {
  return post<SignUpResponse>('/sign-on/service/sign-up', request);
}

export function getOnboardingConfig(options?: { ip?: string }): Promise<OnboardingConfig> {
  return get<OnboardingConfig>('/sign-on/onboarding/config', {
    params: options?.ip ? { ip: options.ip } : undefined,
  });
}

export function patchPersonalInfo(request: PersonalInfoRequest): Promise<void> {
  return patch('/sign-on/service/2.0/personalInfo', request);
}
